import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../services/api';
import './SubscriptionManagement.css';

interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  max_summaries_per_month: number | null;
  features: string[];
  is_trial: boolean;
  is_popular: boolean;
  is_developer_only: boolean;
}

interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: string;
  is_trial: boolean;
  trial_ends_at: string | null;
  current_period_start: string;
  current_period_end: string;
  billing_cycle: string;
  cancel_at_period_end?: boolean;
  canceled_at?: string | null;
}

interface Usage {
  summaries_generated: number;
  period_start: string;
  period_end: string;
}

interface SubscriptionManagementProps {
  onSubscriptionChange?: () => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ onSubscriptionChange }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [processing, setProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once per component lifecycle
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSubscriptionData();
    }
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setError(null);

      // Fetch current subscription
      const subResponse = await apiClient.get('/subscriptions/current');
      
      // If user needs subscription, redirect to home
      if (subResponse.needsSubscription) {
        console.log('User needs subscription, redirecting...');
        window.location.href = '/';
        return;
      }
      
      setSubscription(subResponse.subscription);

      // Fetch usage
      try {
        const usageResponse = await apiClient.get('/subscriptions/usage');
        setUsage(usageResponse.usage);
      } catch (usageError: any) {
        console.error('Error fetching usage:', usageError);
        // Continue even if usage fetch fails
      }

      // Fetch available plans
      const plansResponse = await apiClient.get('/subscriptions/plans');
      setAvailablePlans(plansResponse);
    } catch (error: any) {
      console.error('Error fetching subscription data:', error);
      
      // If user doesn't have a subscription, redirect to home
      if (error.response?.status === 404 || error.response?.data?.needsSubscription) {
        console.log('User needs subscription, redirecting...');
        window.location.href = '/';
        return;
      }
      
      setError('Failed to load subscription information');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      setProcessing(true);
      setError(null);

      // Try to create subscription directly
      await apiClient.post('/subscriptions/create', {
        planId: selectedPlan,
        billingCycle: billingCycle
      });

      // Success - subscription created (either trial or PAYMENT_ENABLED=false)
      await fetchSubscriptionData();
      setShowUpgradeModal(false);
      setSelectedPlan(null);
      
      // Notify parent component of subscription change
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (error: any) {
      console.error('Error upgrading subscription:', error);
      
      // Check if payment is required (402 status)
      if (error.response?.status === 402 && error.response?.data?.requiresPayment) {
        // Redirect to Stripe checkout
        try {
          const checkoutResponse = await apiClient.post('/stripe/create-checkout-session', {
            planId: error.response.data.planId,
            billingCycle: billingCycle
          });
          
          // Redirect to Stripe
          window.location.href = checkoutResponse.url;
        } catch (checkoutError: any) {
          console.error('Error creating checkout session:', checkoutError);
          setError('Failed to initiate payment. Please try again.');
          setProcessing(false);
        }
      } else if (error.response?.data?.trialAlreadyUsed) {
        setError('You have already used your free trial. Please select a paid plan.');
        setProcessing(false);
      } else {
        setError(error.response?.data?.message || error.response?.data?.error || 'Failed to upgrade subscription');
        setProcessing(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (plan: SubscriptionPlan) => {
    if (plan.is_trial || plan.price_monthly === 0) {
      return 'Free';
    }
    const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly / 12;
    return `$${price.toFixed(0)} USD/month`;
  };

  const formatLimit = (limit: number | null) => {
    return limit === null ? 'Unlimited' : limit.toString();
  };

  const getUsagePercentage = () => {
    if (!subscription || !usage) return 0;
    const limit = subscription.plan.max_summaries_per_month;
    if (limit === null) return 0; // Unlimited
    return (usage.summaries_generated / limit) * 100;
  };

  const getRemainingSummaries = () => {
    if (!subscription || !usage) return 0;
    const limit = subscription.plan.max_summaries_per_month;
    if (limit === null) return Infinity;
    return Math.max(0, limit - usage.summaries_generated);
  };

  const getDaysRemaining = () => {
    if (!subscription) return 0;
    const endDate = new Date(subscription.current_period_end);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelProcessing(true);
      setError(null);

      const response = await apiClient.post('/subscriptions/cancel');
      
      // Refresh subscription data
      await fetchSubscriptionData();
      setShowCancelModal(false);
      
      // Show success message
      alert(response.message || 'Subscription canceled successfully');
      
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setProcessing(true);
      setError(null);

      const response = await apiClient.post('/subscriptions/reactivate');
      
      // Refresh subscription data
      await fetchSubscriptionData();
      
      // Show success message
      alert(response.message || 'Subscription reactivated successfully');
      
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Only show loading on initial load, not on re-renders
  if (initialLoading && !subscription) {
    return (
      <div className="subscription-management">
        <div className="loading-state">Loading subscription information...</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-management">
        <div className="error-state">No active subscription found</div>
      </div>
    );
  }

  return (
    <div className="subscription-management">
      <h2>Subscription & Billing</h2>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* Current Plan Section */}
      <div className="subscription-card">
        <div className="card-header">
          <h3>Current Plan</h3>
          {subscription.is_trial && (
            <span className="trial-badge">Trial</span>
          )}
          {subscription.plan.is_popular && (
            <span className="popular-badge">Most Popular</span>
          )}
        </div>

        <div className="plan-details">
          <div className="plan-name-price">
            <h2>{subscription.plan.display_name}</h2>
            <p className="plan-price">
              {subscription.is_trial 
                ? 'Free Trial' 
                : `$${subscription.plan.price_monthly}/month`
              }
            </p>
          </div>

          <p className="plan-description">{subscription.plan.description}</p>

          <div className="plan-info-grid">
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className={`info-value status-${subscription.status}`}>
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Billing Cycle</span>
              <span className="info-value">
                {subscription.billing_cycle === 'none' ? 'Trial' : subscription.billing_cycle.charAt(0).toUpperCase() + subscription.billing_cycle.slice(1)}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Period Start</span>
              <span className="info-value">{formatDate(subscription.current_period_start)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Period End</span>
              <span className="info-value">{formatDate(subscription.current_period_end)}</span>
            </div>
            {subscription.is_trial && subscription.trial_ends_at && (
              <div className="info-item highlight">
                <span className="info-label">Trial Ends</span>
                <span className="info-value">{formatDate(subscription.trial_ends_at)} ({getDaysRemaining()} days left)</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage Section */}
      {usage && (
        <div className="subscription-card">
          <div className="card-header">
            <h3>Current Usage</h3>
            <span className="usage-period">
              {formatDate(usage.period_start)} - {formatDate(usage.period_end)}
            </span>
          </div>

          <div className="usage-details">
            <div className="usage-stat">
              <div className="stat-header">
                <span className="stat-label">AI Summaries Generated</span>
                <span className="stat-value">
                  {usage.summaries_generated} / {formatLimit(subscription.plan.max_summaries_per_month)}
                </span>
              </div>
              {subscription.plan.max_summaries_per_month !== null && (
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${Math.min(100, getUsagePercentage())}%` }}
                  />
                </div>
              )}
              <p className="stat-info">
                {subscription.plan.max_summaries_per_month === null 
                  ? 'Unlimited summaries available'
                  : `${getRemainingSummaries()} summaries remaining this period`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Plan Features */}
      <div className="subscription-card">
        <div className="card-header">
          <h3>Plan Features</h3>
        </div>
        <div className="features-list">
          {subscription.plan.features.map((feature, index) => (
            <div key={index} className="feature-item">
              <span className="feature-icon">✓</span>
              <span className="feature-text">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cancellation Warning */}
      {subscription.cancel_at_period_end && (
        <div className="cancellation-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <h4>Subscription Canceling</h4>
            <p>
              Your subscription will end on {formatDate(subscription.current_period_end)}. 
              You can continue using all features until then.
            </p>
            <button 
              className="btn-secondary"
              onClick={handleReactivateSubscription}
              disabled={processing}
            >
              {processing ? 'Reactivating...' : 'Reactivate Subscription'}
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="subscription-actions">
        <button 
          className="btn-primary"
          onClick={() => setShowUpgradeModal(true)}
        >
          {subscription.is_trial ? 'Upgrade Plan' : 'Change Plan'}
        </button>
        
        {!subscription.cancel_at_period_end && (
          <button 
            className="btn-danger"
            onClick={() => setShowCancelModal(true)}
          >
            {subscription.is_trial ? 'Cancel Trial' : 'Cancel Subscription'}
          </button>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Choose a Plan</h2>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
            </div>

            <div className="billing-toggle">
              <button
                className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('yearly')}
              >
                Yearly <span className="save-badge">Save 17%</span>
              </button>
            </div>

            <div className="plans-list">
              {availablePlans
                .filter(plan => !plan.is_developer_only && plan.id !== subscription.plan.id)
                .map((plan) => (
                  <div
                    key={plan.id}
                    className={`plan-option ${selectedPlan === plan.id ? 'selected' : ''} ${plan.is_popular ? 'popular' : ''}`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {plan.is_popular && <div className="popular-tag">Most Popular</div>}
                    <h3>{plan.display_name}</h3>
                    <p className="plan-price-modal">{formatPrice(plan)}</p>
                    <p className="plan-desc">{plan.description}</p>
                    <div className="plan-limit">
                      {formatLimit(plan.max_summaries_per_month)} AI summaries/month
                    </div>
                    <div className="plan-features-modal">
                      {plan.features.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="feature-mini">✓ {feature}</div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowUpgradeModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleUpgrade}
                disabled={!selectedPlan || processing}
              >
                {processing ? 'Processing...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{subscription.is_trial ? 'Cancel Trial' : 'Cancel Subscription'}</h2>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="cancel-warning-box">
                <div className="warning-icon-large">⚠️</div>
                <h3>Are you sure you want to cancel?</h3>
                {subscription.is_trial ? (
                  <>
                    <p>
                      Your trial will remain active until <strong>{formatDate(subscription.trial_ends_at || subscription.current_period_end)}</strong>.
                    </p>
                    <p>
                      You'll continue to have access to all features until then, and <strong>you will not be charged</strong> when the trial ends.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Your subscription will remain active until <strong>{formatDate(subscription.current_period_end)}</strong>.
                    </p>
                    <p>
                      You'll continue to have access to all features until then, but your subscription will not renew.
                    </p>
                  </>
                )}
                <div className="cancel-details">
                  <div className="detail-item">
                    <span className="detail-label">Current Plan:</span>
                    <span className="detail-value">{subscription.plan.display_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Access Until:</span>
                    <span className="detail-value">{formatDate(subscription.trial_ends_at || subscription.current_period_end)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Days Remaining:</span>
                    <span className="detail-value">{getDaysRemaining()} days</span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="error-message">{error}</div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelProcessing}
              >
                {subscription.is_trial ? 'Keep Trial' : 'Keep Subscription'}
              </button>
              <button 
                className="btn-danger"
                onClick={handleCancelSubscription}
                disabled={cancelProcessing}
              >
                {cancelProcessing ? 'Canceling...' : `Yes, Cancel ${subscription.is_trial ? 'Trial' : 'Subscription'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
